/**
 * SWISS School Portal — authenticated app shell
 * =============================================
 * Hybrid navigation per the unified redesign:
 *
 *   TopNav       — always present: sections, jump-to, crisis, account
 *   SubRail      — only when the current route is inside a section
 *                  that has pages (desktop rail / mobile chip row)
 *   MobileDrawer — hamburger target below 900px
 *   JumpTo       — "/" or the search control
 *
 * Replaces the previous persistent 260px sidebar. The sidebar's
 * responsibilities are redistributed: primary nav → top bar, page
 * nav → sub-rail, school identity + sign-out → account menu, crisis
 * hotline → top bar (desktop) or the sticky footer action (mobile).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Eye, X, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import TopNav from './TopNav';
import SubRail from './SubRail';
import MobileDrawer from './MobileDrawer';
import JumpTo from './JumpTo';
import ModuleLocked from './ModuleLocked';
import { moduleForPath, schoolHasModule } from '../services/tiers';
import {
  SCHOOL_SECTIONS,
  AUDITOR_SECTIONS,
  sectionForPath,
  CRISIS_HOTLINE,
  CRISIS_HOTLINE_TEL,
} from '../config/navigation';
import './Layout.css';

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    viewAsSchool, exitSchoolView, school, isAdmin, isSystemAdmin, systemId, isAuditor,
  } = useAuth();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [jumpOpen, setJumpOpen] = useState(false);

  const sections = isAuditor ? AUDITOR_SECTIONS : SCHOOL_SECTIONS;
  const activeSection = useMemo(
    () => sectionForPath(sections, location.pathname),
    [sections, location.pathname]
  );

  // Programme-tier gate: if the school's tier doesn't include the
  // module for this route, render the upgrade prompt instead of it.
  const moduleKey = moduleForPath(location.pathname);
  const moduleLocked = !!moduleKey && !!school && !schoolHasModule(school, moduleKey);

  // "/" opens the palette, matching the hint rendered in the top bar.
  // Ignored while typing so it doesn't hijack real input.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
      const el = e.target;
      const typing =
        el instanceof HTMLElement &&
        (el.tagName === 'INPUT' ||
          el.tagName === 'TEXTAREA' ||
          el.tagName === 'SELECT' ||
          el.isContentEditable);
      if (typing) return;
      e.preventDefault();
      setJumpOpen(true);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Exit the school-view overlay and land back where the user came
  // from:
  //   MSS / super admin → back to /admin/schools/<id> (the admin
  //                         tabs view they drilled in from)
  //   Group admin       → back to /admin/systems/<theirGroupId>
  //                         (their group dashboard — they don't have
  //                         an /admin landing in the first place)
  //   Anything else     → /admin (fallback for staff impersonating)
  const handleExitView = () => {
    const schoolId = viewAsSchool?.id;
    exitSchoolView();
    if (isAdmin && schoolId) {
      navigate(`/admin/schools/${schoolId}`);
    } else if (isSystemAdmin && systemId) {
      navigate(`/admin/systems/${systemId}`);
    } else {
      navigate('/admin');
    }
  };

  return (
    <div className={`app-shell ${viewAsSchool ? 'has-impersonation-banner' : ''}`}>
      {/* Skip link — invisible until a keyboard user tabs into it */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {viewAsSchool && (
        <div className="impersonation-banner">
          <Eye size={14} />
          <span>
            Viewing as <strong>{school?.shortName || school?.name}</strong> (admin preview · read-only verification)
          </span>
          <button type="button" onClick={handleExitView}>
            <X size={12} />
            <span>Exit admin view</span>
          </button>
        </div>
      )}

      <TopNav
        sections={sections}
        activeSectionKey={activeSection?.key}
        onMenuClick={() => setDrawerOpen(true)}
        onSearchClick={() => setJumpOpen(true)}
      />

      <div className="app-body">
        <SubRail section={activeSection} />

        <main className="page-content" id="main-content" tabIndex="-1">
          {moduleLocked ? <ModuleLocked moduleKey={moduleKey} /> : <Outlet />}
        </main>
      </div>

      {/* Mobile-only crisis action. The top bar hides its hotline
          below 900px so this is the single placement at this
          breakpoint. */}
      <a className="mobile-crisis" href={`tel:${CRISIS_HOTLINE_TEL}`}>
        <Phone size={14} aria-hidden="true" />
        Crisis Support · {CRISIS_HOTLINE}
      </a>

      <MobileDrawer
        open={drawerOpen}
        sections={sections}
        onClose={() => setDrawerOpen(false)}
      />

      <JumpTo
        open={jumpOpen}
        sections={sections}
        onClose={() => setJumpOpen(false)}
      />
    </div>
  );
};

export default Layout;
